import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Map, fromJS } from 'immutable';
import CheckSheet from 'src/screen/Protected/DeviceScreen/TestingReport/CheckSheet';
import FinalizeAndSignOff from 'src/screen/Protected/DeviceScreen/TestingReport/FinalizeAndSignOff';
import FieldReportModal from 'src/screen/Protected/DeviceScreen/Commissioning/FieldReportModal';
import NotesField from 'src/screen/Protected/DeviceScreen/Commissioning/NotesField';
import ProjectInfoTable from 'src/screen/Protected/DeviceScreen/Commissioning/ProjectInfoTable';
import CircuitDynamicTable from 'src/screen/Protected/DeviceScreen/CommissioningCircuit/CircuitDynamicTable';
import { useTranslation } from 'src/i18n';

const REPORT_TYPE = 'audit-eht-circuit';
// A single task type: the paper form has one question block, unlike audit-panel's
// external/internal split.
const VISUAL_TYPE = 'audit-eht-circuit-visual';
const TASK_TYPES = [VISUAL_TYPE];

// Device columns copied into the form when a device is linked, if still empty. The panel tag is
// not among them: this form is created from a circuit, whose record does not carry it.
const PREFILL_FIELDS = [
  { path: ['projectInfo', 'circuitTag'], deviceKey: 'tag' },
  { path: ['projectInfo', 'unit'], deviceKey: 'unit' },
];


const PROJECT_INFO_FIELDS = [
  'circuitTag', 'unit',
  'drawingNumber', 'circuitBreakerNumber',
  'panelTag', 'circuitControllerNumber',
];

// The four electrical readings. Same field/value grid as the project info, pointed at
// extras.electrical.
const ELECTRICAL_FIELDS = [
  'insulationResistance', 'voltageAtPowerBox',
  'currentAtPowerBox', 'busToBusResistance',
];

// Shown inside each reading's input rather than in its label.
const ELECTRICAL_UNITS = {
  insulationResistance: 'MΩ',
  voltageAtPowerBox: 'VAC',
  currentAtPowerBox: 'A',
  busToBusResistance: 'Ω',
};

// The instruments named by the paper form. Labels come from i18n.
const TESTING_DEVICE_KEYS = ['multimeter', 'megger'];

export default function EhtCircuitAuditReport (props) {
  const { t } = useTranslation();

  const tabs = useMemo(() => [
    { value: '1', label: t('ehtCircuitAudit.tabs.projectInfo') },
    { value: '2', label: t('ehtCircuitAudit.tabs.visual') },
    { value: '3', label: t('ehtCircuitAudit.tabs.electrical') },
    { value: '4', label: t('ehtCircuitAudit.tabs.signOff') },
  ], [t]);

  // The two instruments are fixed by the form, so the device column is locked and there is no
  // add or delete — the same shape as the panel audit's Testing Devices table.
  const testingDeviceColumns = useMemo(() => [
    { key: 'device', label: t('ehtCircuitAudit.testingDevices.device'), width: '30%', locked: true },
    { key: 'idNumber', label: t('ehtCircuitAudit.testingDevices.idNumber') },
    { key: 'calDueDate', label: t('ehtCircuitAudit.testingDevices.calDueDate'), type: 'date' },
  ], [t]);

  const buildEmptyResults = useCallback((tasksByType, device) => {
    const tasks = tasksByType.get(VISUAL_TYPE);
    const tasksList = tasks?.toJS ? tasks.toJS() : [];

    return fromJS({
      info: { type: REPORT_TYPE, deviceId: props.deviceId || null },
      // Raw redux rows use `task`; the getCheckSheetTasks selector renames it to `key`.
      [VISUAL_TYPE]: tasksList.map((task: any) => ({
        task_id: task.task ?? task.key, status: null, comment: '',
      })),
      extras: {
        projectInfo: {
          circuitTag: device?.get('tag') || '',
          unit: device?.get('unit') || '',
          drawingNumber: '',
          circuitBreakerNumber: '',
          panelTag: '',
          circuitControllerNumber: '',
        },
        visualComments: { notes: '' },
        electrical: {
          insulationResistance: '',
          voltageAtPowerBox: '',
          currentAtPowerBox: '',
          busToBusResistance: '',
        },
        // The paper form names these two instruments; seeded so the labels match it.
        testingDevices: {
          items: TESTING_DEVICE_KEYS.map(key => ({
            device: t(`ehtCircuitAudit.testingDevices.${key}`), idNumber: '', calDueDate: '',
          })),
        },
      },
      signOff: {},
    }) as Map<string, any>;
  }, [props.deviceId, t]);

  const renderTab = useCallback((tabValue, ctx) => {
    const { allResults, extras, change, readOnly, linkedDeviceTag, handleUnlinkDevice, handleDeviceSelect } = ctx;
    switch (tabValue) {
      case '1':
        return (
          <ProjectInfoTable
            fields={PROJECT_INFO_FIELDS}
            i18nPrefix='ehtCircuitAudit'
            tagField='circuitTag'
            deviceType='circuit'
            readOnly={readOnly}
            data={extras}
            onChange={change}
            linkedDeviceTag={linkedDeviceTag}
            onUnlink={handleUnlinkDevice}
            onDeviceSelect={handleDeviceSelect}
          />
        );
      case '2':
        return (
          <>
            <CheckSheet
              readOnly={readOnly}
              onChange={change}
              id={VISUAL_TYPE}
              title={t('ehtCircuitAudit.visualInspections')}
              data={allResults?.get(VISUAL_TYPE)}
              checkSheetType={VISUAL_TYPE}
            />
            <NotesField
              title={t('ehtCircuitAudit.visualComments')}
              value={extras?.getIn(['visualComments', 'notes'])}
              path={['extras', 'visualComments', 'notes']}
              onChange={change}
              readOnly={readOnly}
            />
          </>
        );
      case '3':
        return (
          <>
            <ProjectInfoTable
              fields={ELECTRICAL_FIELDS}
              i18nPrefix='ehtCircuitAudit'
              sectionKey='electrical'
              labelPath='electrical'
              titleKey='electrical.title'
              units={ELECTRICAL_UNITS}
              readOnly={readOnly}
              data={extras}
              onChange={change}
            />
            <CircuitDynamicTable
              readOnly={readOnly}
              title={t('ehtCircuitAudit.testingDevices.title')}
              columns={testingDeviceColumns}
              data={extras?.getIn(['testingDevices', 'items'])}
              extrasKey={['testingDevices', 'items']}
              onChange={change}
              addable={false}
            />
          </>
        );
      case '4':
        // The paper form's named representatives are dropped: the account that submitted the
        // report is recorded here instead. The visual comments box doubles as the sign-off
        // notes, as the form's only comments section.
        return (
          <FinalizeAndSignOff
            readOnly={readOnly}
            data={allResults?.get('signOff')}
            onChange={change}
            notesLabel={t('ehtCircuitAudit.visualComments')}
            notesValue={extras?.getIn(['visualComments', 'notes'])}
            notesPath={['extras', 'visualComments', 'notes']}
          />
        );
      default:
        return null;
    }
  }, [t, testingDeviceColumns]);

  return (
    <FieldReportModal
      reportType={REPORT_TYPE}
      taskTypes={TASK_TYPES}
      tabs={tabs}
      title={{ label: t('ehtCircuitAudit.modalTitle') }}
      prefillFields={PREFILL_FIELDS}
      requiresStatus={false}
      buildEmptyResults={buildEmptyResults}
      renderTab={renderTab}
      deviceId={props.deviceId}
      reportId={props.reportId}
      open={props.open}
      handleClose={props.handleClose}
    />
  );
}

EhtCircuitAuditReport.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
